import {
  ArrowUp,
  FilePlus2,
  Loader2,
  Mic,
  MicOff,
  TextQuote,
} from "lucide-react";
import type { JSX } from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { dictationMessage } from "@/components/ui/DictationButton";
import { DictationMicMenu } from "@/components/ui/DictationButton/DictationMicMenu";
import { V3Chip } from "@/components/ui/v3/chip";
import {
  DICTATION_MAX_SECONDS,
  useDictation,
} from "@/hooks/transcriber/useDictation";
import { cn } from "@/lib/utils";
import { NoteLanguageMenu } from "../NoteLanguageMenu";
import { useSourcesAttaching } from "../sources/AssistantSources";

const formatSeconds = (seconds: number): string =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const MAX_HEIGHT = 168;

export interface AskSuggestion {
  id: string;
  label: string;
  icon: "shortcut" | "sources";
  onSelect: () => void;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  // While a proposal is being written: a stop square in the send button's
  // place (ChatGPT / Claude); it discards the request.
  proposing?: boolean;
  onStop?: () => void;
  // Applying a proposal: the bar waits.
  disabled?: boolean;
  // While a proposal is being written, typed text can still be sent: it
  // waits its turn (the send button shows once there is text; Stop while
  // the field is empty).
  queueWhileBusy?: boolean;
  placeholder: string;
  // The paperclip (visit sources), left of the field.
  attach?: React.ReactNode;
  // Attached sources as chips, inside the bar above the text.
  attachments?: React.ReactNode;
  // Saved shortcuts and "Add sources to note" above the bar.
  suggestions?: AskSuggestion[];
  // ⌘J / Ctrl+J focuses this bar (one bar listens at a time).
  focusShortcut?: boolean;
  autoFocus?: boolean;
  // The note language chip (the visit note); off for other editors.
  languageChip?: boolean;
  // Label for screen readers (defaults to the placeholder).
  label?: string;
  className?: string;
  testId?: string;
}

// The v3.2 Ask bar (iOS AskBar, style.css .dock[data-mode="ask"]): chat
// style, used under the note and pinned in the assistant. Paperclip, a
// field that grows to six lines, the note language chip, the mic (always,
// so a second dictation adds onto the text) and send once there is text;
// stop, without the mic, while ROSC is writing. Enter sends, Shift+Enter
// starts a new line.
export const AskBar = ({
  value,
  onChange,
  onSend,
  proposing = false,
  onStop,
  disabled = false,
  queueWhileBusy = false,
  placeholder,
  attach,
  attachments,
  suggestions = [],
  focusShortcut = false,
  autoFocus = false,
  languageChip = true,
  label,
  className,
  testId = "ask-bar",
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const field = useRef<HTMLTextAreaElement>(null);
  const dictation = useDictation();
  const typedBefore = useRef("");
  const { status, error, elapsedSeconds } = dictation;
  const dictating = status !== "idle";
  const listening = status === "listening";
  const hasText = value.trim().length > 0;

  // Dictated words go after what was in the field when the mic was
  // pressed (typed, or an earlier dictation), with a space; never over it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: follows the dictated text only.
  useEffect(() => {
    if (dictation.text)
      onChange(
        [typedBefore.current.trimEnd(), dictation.text.trim()]
          .filter(Boolean)
          .join(" "),
      );
  }, [dictation.text]);

  // Grows with the text, up to MAX_HEIGHT, then scrolls.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured per value.
  useLayoutEffect(() => {
    const element = field.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_HEIGHT)}px`;
  }, [value]);

  useEffect(() => {
    if (!focusShortcut) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === "j"
      ) {
        event.preventDefault();
        field.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [focusShortcut]);

  useEffect(() => {
    if (autoFocus) field.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  const toggleDictation = (): void => {
    if (dictating) {
      dictation.stop();
      return;
    }
    typedBefore.current = value;
    void dictation.start();
  };

  // Files still attaching (fast attach): the question waits for them, so
  // it never goes out without a file the clinician just added.
  const attaching = useSourcesAttaching();
  const send = (): void => {
    if (!hasText || disabled || attaching) return;
    if (proposing && !queueWhileBusy) return;
    if (dictating) dictation.stop();
    onSend();
  };

  const status_ =
    dictating || error
      ? dictationMessage(t, { status, error, elapsedSeconds })
      : null;
  const showSuggestions =
    suggestions.length > 0 && !proposing && !dictating && !hasText;
  // The mic stays after a dictation (clinician request 05/11), next to
  // send once there is text, so more can be dictated onto the end. It goes
  // while a request is being sent or written (Stop, or a queued send).
  const showStop = proposing && !(queueWhileBusy && hasText);
  const showSend = !showStop && hasText && !dictating;
  const showMic = !proposing;
  // One 36px control size for the paperclip, language chip and mic/send, so
  // the row has one centre line; an invisible 44px tap area around each.
  const round =
    "relative inline-flex size-9 shrink-0 items-center justify-center rounded-full after:absolute after:-inset-1 after:content-['']";

  return (
    <div
      className={cn(
        "mx-auto flex w-full max-w-[648px] flex-col gap-2",
        className,
      )}
      data-testid={testId}
    >
      {status_ && (
        <p
          aria-live="polite"
          className={cn(
            "flex items-center gap-2 self-start rounded-full bg-v3-panel px-3 py-1.5 text-[12.5px] text-v3-text-2 shadow-v3-float",
            error && !dictating && "text-v3-rec-text",
          )}
        >
          {listening && (
            <span className="size-1.5 animate-pulse rounded-full bg-v3-rec" />
          )}
          <span>{status_}</span>
          {listening && (
            <span className="tabular-nums text-v3-text-3">
              {formatSeconds(elapsedSeconds)} /{" "}
              {formatSeconds(DICTATION_MAX_SECONDS)}
            </span>
          )}
        </p>
      )}
      {showSuggestions && (
        <fieldset className="flex min-w-0 gap-1.5 overflow-x-auto pb-0.5 scrollbar-none [mask-image:linear-gradient(90deg,#000_90%,transparent)]">
          <legend className="sr-only">{t("v3.ask.suggestions")}</legend>
          {suggestions.map((suggestion) => (
            <V3Chip
              key={suggestion.id}
              data-testid="ask-suggestion"
              variant="suggestion"
              onClick={() => {
                suggestion.onSelect();
                field.current?.focus();
              }}
            >
              {suggestion.icon === "sources" ? (
                <FilePlus2 aria-hidden />
              ) : (
                <TextQuote aria-hidden />
              )}
              {suggestion.label}
            </V3Chip>
          ))}
        </fieldset>
      )}
      <div
        className={cn(
          "@container rounded-[24px] bg-v3-panel shadow-v3-float transition-shadow",
          listening
            ? "ring-[1.5px] ring-[color-mix(in_srgb,var(--v3-rec)_45%,transparent)]"
            : "ring-1 ring-v3-border focus-within:ring-[1.5px] focus-within:ring-v3-brand-ring",
        )}
      >
        {attachments && <div className="px-3 pt-2.5">{attachments}</div>}
        {/* By the composer's own width (a container query, so a side panel
            behaves like a phone). From 480px: one row, 52px high on one line,
            8px padding around 36px controls and a 36px one-line field; the
            controls sit on the last line when the field grows. Narrower: the
            field on its own full-width row, then attach on the left and the
            language and mic on the right. */}
        <div
          className="flex flex-wrap items-center gap-x-1 gap-y-0.5 p-2 @min-[480px]:flex-nowrap @min-[480px]:items-end @min-[480px]:gap-y-1 [&_[data-testid=note-language-chip]]:relative [&_[data-testid=note-language-chip]]:size-9! [&_[data-testid=note-language-chip]]:after:absolute [&_[data-testid=note-language-chip]]:after:-inset-1 [&_[data-testid=note-language-chip]]:after:content-[''] [&_[data-testid=source-attach]]:relative [&_[data-testid=source-attach]]:size-9! [&_[data-testid=source-attach]]:min-h-0! [&_[data-testid=source-attach]]:after:absolute [&_[data-testid=source-attach]]:after:-inset-1 [&_[data-testid=source-attach]]:after:content-['']"
          data-testid={`${testId}-row`}
        >
          {attach && (
            <div className="flex h-9 shrink-0 items-center">{attach}</div>
          )}
          <label className="sr-only" htmlFor={`${testId}-field`}>
            {label ?? placeholder}
          </label>
          <textarea
            ref={field}
            aria-keyshortcuts={focusShortcut ? "Meta+J Control+J" : undefined}
            className="order-first block h-9 max-h-[168px] min-h-9 w-full min-w-0 basis-full resize-none border-0 bg-transparent px-2 py-2 font-v3 text-[16px] leading-5 text-v3-text outline-hidden placeholder:text-v3-text-3 @min-[480px]:order-none @min-[480px]:w-auto @min-[480px]:flex-1 @min-[480px]:basis-0 lg:text-[15px]"
            data-testid={`${testId}-field`}
            disabled={disabled}
            id={`${testId}-field`}
            placeholder={placeholder}
            readOnly={dictating}
            rows={1}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !event.shiftKey &&
                !event.nativeEvent.isComposing
              ) {
                event.preventDefault();
                send();
              }
            }}
          />
          <div className="ml-auto flex shrink-0 items-end gap-1">
            {languageChip && (
              <NoteLanguageMenu
                disabled={dictating || disabled}
                variant="chip"
              />
            )}
            {showMic && (
              <>
                <DictationMicMenu
                  disabled={dictating || disabled}
                  variant="v3"
                />
                <button
                  aria-label={t(
                    dictating ? "dictation.stop" : "dictation.start",
                  )}
                  aria-pressed={dictating}
                  className={round}
                  data-testid={`${testId}-mic`}
                  disabled={status === "finishing" || disabled}
                  title={t(dictating ? "dictation.stop" : "dictation.start")}
                  type="button"
                  onClick={toggleDictation}
                >
                  <span
                    className={cn(
                      "inline-flex size-9 items-center justify-center rounded-full",
                      listening
                        ? "bg-v3-rec text-white ring-4 ring-[color-mix(in_srgb,var(--v3-rec)_30%,transparent)]"
                        : "bg-v3-panel text-v3-text shadow-[inset_0_0_0_1px_var(--v3-border-strong),0_1px_2px_rgba(0,0,0,0.06)]",
                    )}
                  >
                    {listening ? (
                      <span className="size-3 rounded-[3px] bg-white" />
                    ) : dictating ? (
                      <Loader2 aria-hidden className="size-4 animate-spin" />
                    ) : error ? (
                      <MicOff
                        aria-hidden
                        className="size-[18px] text-v3-rec-text"
                      />
                    ) : (
                      <Mic aria-hidden className="size-[18px]" />
                    )}
                  </span>
                </button>
              </>
            )}
            {showStop ? (
              <button
                aria-label={t("v3.ask.stop")}
                className={round}
                data-testid={`${testId}-stop`}
                title={t("v3.ask.stop")}
                type="button"
                onClick={onStop}
              >
                <span className="inline-flex size-9 items-center justify-center rounded-full bg-v3-ink">
                  <span className="size-[11px] rounded-[3px] bg-v3-on-ink" />
                </span>
              </button>
            ) : showSend ? (
              <button
                aria-label={t("v3.ask.send")}
                className={cn(round, "disabled:opacity-45")}
                data-testid={`${testId}-send`}
                disabled={disabled || attaching}
                title={
                  attaching
                    ? t("visit.workspace.sources.waitForFiles")
                    : t("v3.ask.send")
                }
                type="button"
                onClick={send}
              >
                <span className="inline-flex size-9 items-center justify-center rounded-full bg-v3-ink text-v3-on-ink">
                  <ArrowUp
                    aria-hidden
                    className="size-[18px]"
                    strokeWidth={2.4}
                  />
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};
